import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Checkbox, Input, InputNumber, Modal } from 'antd';

import FormDefinitionEditor, {
	blankDefinition,
	definitionFrom,
	staleNameOf,
	useFormDefinition,
} from './FormDefinitionEditor';
import { createPublicForm, updatePublicForm } from '../../services/publicForms';
import { definitionPayload } from '../../services/shareLinks';
import { normalizeId } from '../../utils/ids';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import {
	addressUrl,
	defaultLimits,
	describePublicFormError,
	limitsProblem,
	slugProblem,
	suggestSlug,
	LIMIT_BOUNDS,
	MAX_FORM_NAME,
} from '../../utils/publicForms';

const blankState = ({ suggestedCollectionSlug = '' } = {}) => ({
	...blankDefinition(),
	name: '',
	slug: '',
	// Follows the name until the manager types an address of their own.
	slugTouched: false,
	collectionSlug: suggestedCollectionSlug || '',
	limits: defaultLimits(),
	acknowledge: false,
});

// A public form carries its definition under `definition` (keepr-api SCHEMA
// § The public form row); the editor reads the same keys an invite link has
// at its top level.
const stateFromForm = form => ({
	...definitionFrom(form?.definition, form?.card_id),
	name: form?.name || '',
	slug: form?.address?.slug || '',
	slugTouched: true,
	collectionSlug: form?.address?.collectionSlug || '',
	limits: { ...defaultLimits(), ...(form?.limits || {}) },
	acknowledge: true,
});

/**
 * Make, or change, a PERMANENT PUBLIC FORM (KPR-513; keepr-api docs/SCHEMA.md
 * § Public forms; the owner's rulings f01–f03, f05, f08). The form an invite
 * link is — the same FormDefinitionEditor, the same rules, the API's same
 * module — at an address that never expires, for a standing audience.
 *
 * What is this kind's own, around the shared editor:
 *   - the warning says what an address that is NOT secret means: anyone who
 *     reads it can send a submission, so the bot check, the limits and the
 *     review queue are the whole defence;
 *   - a NAME, required (an invite link's is optional);
 *   - the ADDRESS, on a create: the collection's slug — asked only while the
 *     collection has none, because it is chosen once and every printed sign
 *     carries it — and the form's, suggested from its name. An edit shows
 *     the address and changes it through Point address here… in the list,
 *     never here (the API's PATCH refuses it);
 *   - the LIMITS (f03): a day, a week, and how many may wait for review
 *     before the address pauses itself;
 *   - on a create, the manager's acknowledgment, which the API requires.
 *
 * `form` present = edit (PATCH). The card never changes on an edit.
 */
export default function PublicFormModal({
	open,
	onClose,
	onSaved,
	collectionId,
	form = null,
	collectionSlug = null,
	suggestedCollectionSlug = null,
	cardOptions = [],
	cardsById = {},
	getElementsForCard = null,
}) {
	const editing = Boolean(form);
	const [state, setState] = useState(() => blankState({ suggestedCollectionSlug }));
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);
	const [seedVersion, setSeedVersion] = useState(0);
	const model = useFormDefinition({ state, setState, collectionId, cardOptions, cardsById, getElementsForCard });
	const { set, published, cardId, problem: definitionProblem, definition, elementsByName, setRefused } = model;

	useEffect(() => {
		if (!open) return;
		setState(editing ? stateFromForm(form) : blankState({ suggestedCollectionSlug }));
		setError(null);
		setSaving(false);
		setRefused([]);
		setSeedVersion(version => version + 1);
	}, [open, editing, form, suggestedCollectionSlug, setRefused]);

	const { resetDirty, guardDiscard } = useDirtyForm(state, { enabled: open, baselineKey: seedVersion });
	const handleCancel = useCallback(() => guardDiscard(() => onClose?.()), [guardDiscard, onClose]);

	// The collection's slug is asked for only while it has none.
	const needsCollectionSlug = !editing && !collectionSlug;
	const chosenCollectionSlug = needsCollectionSlug ? state.collectionSlug.trim() : (collectionSlug || '');

	const setName = (name) => setState(prev => ({
		...prev,
		name,
		slug: prev.slugTouched ? prev.slug : suggestSlug(name),
	}));

	const nameProblem = !state.name.trim() ? 'Give the form a name.' : null;
	const addressProblem = editing ? null
		: (needsCollectionSlug ? slugProblem(state.collectionSlug, 'The collection’s address') : null)
			|| slugProblem(state.slug, 'The form’s address');
	const limitProblem = limitsProblem(state.limits);
	const ackProblem = !editing && !state.acknowledge ? 'Tick the box above Create form to say you know anyone with the address can send a submission.' : null;
	const problem = !cardId ? null : (definitionProblem || nameProblem || addressProblem || limitProblem || ackProblem);
	const ready = Boolean(cardId) && !problem;

	const preview = useMemo(() => {
		if (editing) return addressUrl(form?.address);
		const c = chosenCollectionSlug || '…';
		const f = state.slug.trim() || '…';
		return addressUrl({ path: `/f/${c}/${f}` });
	}, [editing, form, chosenCollectionSlug, state.slug]);

	// An edit sends `definition` only when it changed. The API judges a live
	// or paused form's definition with the going-live gates (the server's
	// switch, the bot check, the collection's intake switch), so sending it
	// on every save would refuse a rename or a lowered limit exactly when a
	// manager most needs one — while the form is down for one of those
	// reasons (independent review, KPR-513). Compared as the API would read
	// it: both sides through the same payload builder.
	const definitionChanged = useMemo(() => {
		if (!editing) return true;
		const stored = form?.definition || {};
		const before = definitionPayload({ ...stored, uploads: stored.uploads || undefined }, { edit: true });
		return JSON.stringify(before) !== JSON.stringify(definitionPayload(definition, { edit: true }));
	}, [editing, form, definition]);

	const handleSave = useCallback(async () => {
		if (!ready) return;
		setSaving(true);
		setError(null);
		try {
			const result = editing
				? await updatePublicForm(collectionId, normalizeId(form._id), { name: state.name, definition: definitionChanged ? definition : undefined, limits: state.limits })
				: await createPublicForm(collectionId, {
					name: state.name,
					cardId,
					definition,
					limits: state.limits,
					slug: state.slug,
					collectionSlug: needsCollectionSlug ? state.collectionSlug : undefined,
					acknowledge: state.acknowledge,
				});
			resetDirty();
			onSaved?.(result, { created: !editing });
		} catch (err) {
			const staleName = staleNameOf(err);
			if (staleName) setRefused(prev => (prev.includes(staleName) ? prev : [...prev, staleName]));
			setError(describePublicFormError(err, elementsByName));
		} finally {
			setSaving(false);
		}
	}, [ready, editing, collectionId, form, state, definition, definitionChanged, cardId, needsCollectionSlug, resetDirty, onSaved, setRefused, elementsByName]);

	const limitInput = (key, label, unit) => {
		const [min, max] = LIMIT_BOUNDS[key];
		return (
			<div>
				<label className="share-links__field-label" htmlFor={`public-form-${key}`}>{label}</label>
				<InputNumber
					id={`public-form-${key}`}
					min={min}
					max={max}
					precision={0}
					value={state.limits[key]}
					onChange={value => set({ limits: { ...state.limits, [key]: Number.isInteger(value) ? value : LIMIT_BOUNDS[key][2] } })}
					addonAfter={unit}
				/>
			</div>
		);
	};

	return (
		<Modal
			open={open}
			title={editing ? 'Edit public form' : 'New public form'}
			okText={editing ? 'Save changes' : 'Create form'}
			okButtonProps={{ disabled: !ready, 'data-action': 'public-form-save' }}
			confirmLoading={saving}
			onOk={handleSave}
			onCancel={handleCancel}
			destroyOnClose
			width={620}
			className="share-links__modal intake-link-modal public-form-modal"
		>
			<Alert
				className="intake-link-modal__warning"
				type="warning"
				showIcon
				message="Anyone who reads the address can add an item"
				description={(
					<>
						The address is not secret: it goes on signs, posters and pages, and it never expires. Every
						submission passes a bot check, the limits below cap how many arrive, and each one waits in this
						collection’s review queue — out of the list, counts, exports and public views — until someone
						accepts it.
						{published ? (
							<span className="intake-link-modal__published" data-testid="public-form-published">
								{' '}This collection is published: once accepted, a submission is read by everyone who uses
								keepr. Ask for names and contact details privately.
							</span>
						) : null}
					</>
				)}
			/>

			{editing && form?.makerLeft ? (
				<Alert
					className="public-form-modal__maker-left"
					type="info"
					showIcon
					message="Whoever made this form no longer manages this collection"
					description="It keeps working as it is: the collection owns it, and its managers run it."
					data-testid="public-form-maker-left"
				/>
			) : null}

			<FormDefinitionEditor
				definition={model}
				editing={editing}
				noun="form"
				idPrefix="public-form"
				onCardChosen={() => setError(null)}
				nameField={(
					<div className="share-links__field">
						<label className="share-links__field-label" htmlFor="public-form-name">Name</label>
						<Input
							id="public-form-name"
							maxLength={MAX_FORM_NAME}
							value={state.name}
							placeholder="e.g. Report a pothole"
							onChange={event => setName(event.target.value)}
						/>
						<p className="share-links__hint">It names the form in this list and on every item it brings in. The visitor reads the headline.</p>
					</div>
				)}
			/>

			<div className="share-links__field public-form-modal__address" data-testid="public-form-address">
				<span className="share-links__field-label">Address</span>
				{editing ? (
					<p className="public-form-modal__preview">
						{preview || 'No address. Give it one with Point address here… in the list.'}
					</p>
				) : (
					<>
						<div className="intake-link-modal__row public-form-modal__slugs">
							{needsCollectionSlug ? (
								<div>
									<label className="share-links__field-label" htmlFor="public-form-collection-slug">This collection</label>
									<Input
										id="public-form-collection-slug"
										maxLength={48}
										value={state.collectionSlug}
										placeholder="e.g. salem-reports"
										onChange={event => set({ collectionSlug: event.target.value.toLowerCase() })}
									/>
								</div>
							) : null}
							<div>
								<label className="share-links__field-label" htmlFor="public-form-slug">This form</label>
								<Input
									id="public-form-slug"
									maxLength={48}
									value={state.slug}
									placeholder="e.g. report-a-pothole"
									onChange={event => set({ slug: event.target.value.toLowerCase(), slugTouched: true })}
								/>
							</div>
						</div>
						<p className="public-form-modal__preview" data-testid="public-form-preview">{preview}</p>
						<p className="share-links__hint">
							Lower-case letters, digits and hyphens.
							{needsCollectionSlug ? ' The collection’s part is chosen once, for every form it will ever have: it cannot change later, because printed signs carry it.' : ''}
							{' '}No address is ever given to anyone else, even after this form is retired.
						</p>
					</>
				)}
			</div>

			<div className="share-links__field">
				<span className="share-links__field-label">Limits</span>
				<div className="intake-link-modal__row public-form-modal__limits">
					{limitInput('perDay', 'A day, at most', 'submissions')}
					{limitInput('perWeek', 'A week, at most', 'submissions')}
					{limitInput('maxPending', 'Waiting for review, at most', 'submissions')}
				</div>
				<p className="share-links__hint">
					When a day’s or a week’s submissions are used up, the form says so and asks visitors to come back
					later. When as many wait for review as you allow, the address pauses itself, and starts again on its
					own once someone has reviewed some.
				</p>
			</div>

			{!editing ? (
				<div className="share-links__field">
					<Checkbox
						checked={state.acknowledge}
						onChange={event => set({ acknowledge: event.target.checked })}
						data-testid="public-form-acknowledge"
					>
						I understand that anyone with the address can send this collection a submission.
					</Checkbox>
				</div>
			) : null}

			{problem && cardId ? <p className="share-links__hint intake-link-modal__problem" data-testid="public-form-problem">{problem}</p> : null}
			{error ? <Alert className="intake-link-modal__error" type="error" showIcon message={error} data-testid="public-form-error" /> : null}
		</Modal>
	);
}
